'use client';
import Image from 'next/image';
import styles from './gallery.module.scss';
import { useKeenSlider, KeenSliderPlugin, KeenSliderInstance } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useBP } from 'scripts';
import { Image as ImageTag, Spin } from 'antd';
import { AttachmentType, AttachmentTypeEnum } from 'types';
import { TbCamera } from 'react-icons/tb';

function GalleryComp({ gallery, className }: { gallery?: AttachmentType[]; className?: string }) {
    const [loading, setLoading] = useState(true);
    const [previewOpen, setPreviewOpen] = useState(false);
    const closingFromBack = useRef(false);
    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,
        rtl: true,
    });
    const bp = useBP();
    function ThumbnailPlugin(mainRef: MutableRefObject<KeenSliderInstance | null>): KeenSliderPlugin {
        return (slider) => {
            function removeActive() {
                slider.slides.forEach((slide) => {
                    slide.classList.remove('active');
                });
            }
            function addActive(idx: number) {
                slider.slides[idx].classList.add('active');
            }

            function addClickEvents() {
                slider.slides.forEach((slide, idx) => {
                    slide.addEventListener('click', () => {
                        if (mainRef.current) mainRef.current.moveToIdx(idx);
                    });
                });
            }

            slider.on('created', () => {
                if (!mainRef.current) return;
                addActive(slider.track.details.rel);
                addClickEvents();
                mainRef.current.on('animationStarted', (main) => {
                    removeActive();
                    const next = main.animator.targetIdx || 0;
                    addActive(main.track.absToRel(next));
                    slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
                });
            });
        };
    }
    const [thumbnailRef] = useKeenSlider<HTMLDivElement>(
        {
            initial: 0,
            rtl: true,
            slides: {
                perView: 4,
                spacing: 24,
            },
        },
        [ThumbnailPlugin(instanceRef)],
    );

    useEffect(() => {
        instanceRef.current?.update();
        if (bp) setLoading(false);
    }, [bp?.bp]);

    useEffect(() => {
        if (!previewOpen) return;

        window.history.pushState({ galleryPreview: true }, '');

        const onPopState = () => {
            closingFromBack.current = true;
            setPreviewOpen(false);
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [previewOpen]);

    const onPreviewVisibleChange = (visible: boolean) => {
        if (visible) {
            setPreviewOpen(true);
            return;
        }
        if (closingFromBack.current) {
            closingFromBack.current = false;
            return;
        }
        setPreviewOpen(false);
        window.history.back();
    };

    return (
        <div className={`${styles['slider-layout']} ${className}`}>
            {loading ? (
                <div className={`${styles['loading']} center-content`}>
                    <Spin className="d-flex-i center-content" />
                </div>
            ) : (
                <>
                    <div ref={sliderRef} className={`${styles['viewer-layout']} mb-3 keen-slider`}>
                        {gallery?.length ? (
                            <ImageTag.PreviewGroup
                                preview={{
                                    visible: previewOpen,
                                    onVisibleChange: onPreviewVisibleChange,
                                    imageRender: (original, info) =>
                                        gallery[info.current].type === AttachmentTypeEnum.video ? (
                                            <video
                                                autoPlay
                                                controls
                                                src={gallery[info.current].file}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    maxHeight: '70vh',
                                                    maxWidth: 700,
                                                }}
                                            />
                                        ) : (
                                            original
                                        ),
                                    toolbarRender: () => null,
                                    destroyOnClose: true,
                                }}
                            >
                                <>
                                    {gallery
                                        .sort((a, b) => a.id - b.id)
                                        .map((img) => (
                                            <div key={img.id} className={`keen-slider__slide ${styles.image}`}>
                                                <div style={{ position: 'relative' }}>
                                                    {img.type === AttachmentTypeEnum.video ? (
                                                        <video
                                                            controls={false}
                                                            src={img.file}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={img.file}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                            alt=""
                                                        />
                                                    )}
                                                    <ImageTag
                                                        src={img.file}
                                                        wrapperStyle={{
                                                            width: '100%',
                                                            height: '100%',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            objectFit: 'cover',
                                                            opacity: 0,
                                                        }}
                                                        alt={img.alt}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                </>
                            </ImageTag.PreviewGroup>
                        ) : (
                            <div
                                className={`keen-slider__slide center-content bg-gray3 bg-d-black3 ${styles['no-img']}`}
                            >
                                <TbCamera size={32} className="t-l-gray1" />
                            </div>
                        )}
                    </div>
                    <div
                        ref={thumbnailRef}
                        className={`keen-slider thumbnail d-pre-md-none-i ${gallery?.length ? '' : 'd-none-i'}`}
                    >
                        {gallery
                            ?.sort((a, b) => a.id - b.id)
                            .map((img) => (
                                <div key={img.id} className={`${styles['img']} keen-slider__slide pointer`}>
                                    {img.type === AttachmentTypeEnum.video ? (
                                        <video
                                            controls={false}
                                            src={img.file}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <Image src={img.file} width={102} height={102} alt="" />
                                    )}
                                </div>
                            ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default GalleryComp;
